import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Tooltip, makeStyles, mergeClasses } from '@fluentui/react-components';
import { useColorScheme } from './ThemeProvider.js';
import { tokens } from './tokens.js';
import { ShellIcons } from './shell/icons.js';
import { shellMetrics, shellTokens } from './shell/tokens.js';
import { useShellLayout } from './shell/useShellLayout.js';
import { useFold, usePrefersReducedMotion } from './shell/motion.js';
/** The line under the app name when the build gives none: what the whole product is about. */
const DEFAULT_CAPTION = 'Compliance & Onboarding';
function AnchorLink({ to, children, ...rest }) {
    return (_jsx("a", { href: to, ...rest, children: children }));
}
/** The words in the menu, faded while it folds (#742). An attribute, so one rule on the menu reaches every one of them. */
const LABEL = { 'data-shell-label': '' };
/** How the band and the fold move: Fluent's own duration and curve (#742, drawing H1). */
const motion = {
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveDecelerateMid,
};
const useStyles = makeStyles({
    shell: {
        display: 'flex',
        height: '100dvh',
        overflow: 'clip',
        backgroundColor: shellTokens.canvas,
        color: shellTokens.text1,
    },
    nav: {
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        backgroundColor: shellTokens.surface,
        height: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
    },
    navExpanded: { width: shellMetrics.sidebarWidth },
    navRail: { width: shellMetrics.railWidth, alignItems: 'center' },
    /** The fold: the width moves, and the words fade as it goes (#742). Not applied under reduced motion. */
    navMoving: { transitionProperty: 'width', ...motion },
    navFolding: { '& [data-shell-label]': { opacity: 0, transitionProperty: 'opacity', ...motion } },
    navUnfolding: {
        '& [data-shell-label]': {
            animationName: { from: { opacity: 0 }, to: { opacity: 1 } },
            animationDuration: tokens.durationNormal,
            animationTimingFunction: tokens.curveDecelerateMid,
        },
    },
    /** VIGEO HR and the fold button beside it, open or folded (#742: today's foot button moved up). */
    identity: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalS} ${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    },
    identityRail: {
        flexDirection: 'column',
        width: '100%',
        gap: tokens.spacingVerticalXS,
        padding: `${tokens.spacingVerticalM} 0 ${tokens.spacingVerticalXS}`,
    },
    brandLink: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalMNudge,
        flexGrow: 1,
        minWidth: 0,
        color: shellTokens.text1,
        textDecorationLine: 'none',
        borderRadius: tokens.borderRadiusLarge,
        ':focus-visible': { outline: `2px solid ${shellTokens.focusRing}`, outlineOffset: '2px' },
    },
    brandLinkRail: { flexGrow: 0 },
    brandText: { display: 'flex', flexDirection: 'column', minWidth: 0, whiteSpace: 'nowrap' },
    brandName: { fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, fontWeight: tokens.fontWeightSemibold },
    caption: { fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, color: shellTokens.text3 },
    mark: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: '32px',
        height: '32px',
        borderRadius: tokens.borderRadiusLarge,
        backgroundColor: shellTokens.action,
        color: shellTokens.avatarForeground,
        fontWeight: tokens.fontWeightBold,
        fontSize: tokens.fontSizeBase300,
    },
    /** A thin line: under the folded menu's header, and where a group heading would be on the icon rail. */
    railDivider: { width: '40px', height: tokens.strokeWidthThin, flexShrink: 0, backgroundColor: shellTokens.divider, margin: `${tokens.spacingVerticalXS} auto` },
    list: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    },
    listRail: { width: '100%', alignItems: 'center', paddingLeft: 0, paddingRight: 0 },
    /** One run of rows under a heading; spaced as the list is, so a group adds no gap of its own. */
    section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
    sectionRail: { alignItems: 'center' },
    heading: {
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM} ${tokens.spacingVerticalXS}`,
        fontSize: tokens.fontSizeBase100,
        lineHeight: tokens.lineHeightBase100,
        fontWeight: tokens.fontWeightSemibold,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: shellTokens.text3,
        whiteSpace: 'nowrap',
    },
    /**
     * The current page's band, behind the rows (#742). One element for the whole menu, moved to whichever row is the
     * current page, so going to a new page slides it there. Its place is measured from that row.
     */
    band: {
        position: 'absolute',
        top: 0,
        left: 0,
        borderRadius: tokens.borderRadiusLarge,
        backgroundColor: shellTokens.navActiveBackground,
        pointerEvents: 'none',
    },
    /** The 3 × 16 bar on the band's left: with the heavier name, what says "current" without colour. */
    bar: {
        position: 'absolute',
        left: 0,
        top: '50%',
        width: '3px',
        height: '16px',
        marginTop: '-8px',
        borderRadius: tokens.borderRadiusSmall,
        backgroundColor: shellTokens.navActiveMarker,
    },
    item: {
        // Above the band, so the band never hides the row's words or its focus ring.
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalMNudge,
        minHeight: shellMetrics.navRowHeight,
        padding: `0 ${tokens.spacingHorizontalM}`,
        borderRadius: tokens.borderRadiusLarge,
        color: shellTokens.text2,
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightMedium,
        textDecorationLine: 'none',
        ':hover': { backgroundColor: shellTokens.hover, color: shellTokens.text1 },
        ':focus-visible': { outline: `2px solid ${shellTokens.focusRing}`, outlineOffset: '2px' },
    },
    /** The band behind it carries the colour; the row keeps it through a hover. */
    itemActive: {
        color: shellTokens.navActiveForeground,
        fontWeight: tokens.fontWeightSemibold,
        ':hover': { backgroundColor: 'transparent', color: shellTokens.navActiveForeground },
    },
    /** Switched off: greyed, and the hover that invites a click is undone. The words carry the meaning, not the colour. */
    itemUnavailable: {
        color: shellTokens.textDisabled,
        cursor: 'default',
        ':hover': { backgroundColor: 'transparent', color: shellTokens.textDisabled },
    },
    /** The reason stays on one line and in the ordinary caption colour, so the words are easy to read beside a greyed name. */
    reason: { whiteSpace: 'nowrap', flexShrink: 0 },
    /** A menu icon is drawn at 20 (#742, H1's numbers), from the shell's 16-grid set, with its stroke kept as fine. */
    icon: { display: 'inline-flex', flexShrink: 0, '& svg': { width: '20px', height: '20px', strokeWidth: 1.3 } },
    /** A switched-off row under a group is indented less than a link there: "Candidates" and its reason must fit on one line. */
    itemNestedUnavailable: { paddingLeft: tokens.spacingHorizontalXL },
    itemRail: { width: shellMetrics.touchTarget, minHeight: shellMetrics.touchTarget, justifyContent: 'center', padding: 0 },
    itemMobile: { minHeight: shellMetrics.touchTarget },
    /** A disclosure, not a link: a <button> needs the chrome reset that an <a> does not. */
    groupToggle: {
        width: '100%',
        backgroundColor: 'transparent',
        borderTopStyle: 'none', borderRightStyle: 'none', borderBottomStyle: 'none', borderLeftStyle: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        textAlign: 'left',
    },
    /** No icon of its own: the name lines up under the group's name (padding, a 20 icon and the gap; drawing N3). */
    itemNested: { paddingLeft: `calc(${tokens.spacingHorizontalM} + 20px + ${tokens.spacingHorizontalMNudge})` },
    group: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
    chevron: { display: 'inline-flex', color: shellTokens.text2, transitionProperty: 'transform', transitionDuration: tokens.durationFaster },
    chevronOpen: { transform: 'rotate(180deg)' },
    label: { flexGrow: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '18px',
        height: '18px',
        padding: `0 ${tokens.spacingHorizontalXS}`,
        borderRadius: '9px',
        backgroundColor: shellTokens.badgeBackground,
        color: shellTokens.badgeForeground,
        fontSize: tokens.fontSizeBase100,
        lineHeight: tokens.lineHeightBase100,
        fontWeight: tokens.fontWeightBold,
    },
    badgeRail: { position: 'absolute', top: '2px', right: '2px', minWidth: '16px', height: '16px' },
    spacer: { flexGrow: 1 },
    /** The foot: Dark theme, then the person's card with Sign out (choice 1 B on #715). */
    utility: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM} ${tokens.spacingVerticalM}`,
        borderTop: `${tokens.strokeWidthThin} solid ${shellTokens.divider}`,
    },
    utilityRail: { width: '100%', alignItems: 'center', paddingLeft: 0, paddingRight: 0, borderTopStyle: 'none' },
    control: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalMNudge,
        minHeight: shellMetrics.navRowHeight,
        padding: `0 ${tokens.spacingHorizontalM}`,
        borderRadius: tokens.borderRadiusMedium,
        border: 'none',
        backgroundColor: 'transparent',
        color: shellTokens.text2,
        fontFamily: tokens.fontFamilyBase,
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightMedium,
        cursor: 'pointer',
        textDecorationLine: 'none',
        ':hover': { backgroundColor: shellTokens.hover, color: shellTokens.text1 },
        ':focus-visible': { outline: `2px solid ${shellTokens.focusRing}`, outlineOffset: '2px' },
    },
    controlRail: { flexShrink: 0, width: shellMetrics.touchTarget, minHeight: shellMetrics.touchTarget, justifyContent: 'center', padding: 0 },
    controlSmall: { flexShrink: 0, minHeight: '32px', padding: `0 ${tokens.spacingHorizontalS}`, color: shellTokens.text3 },
    themeRow: { width: '100%', textAlign: 'left' },
    /** The drawn switch beside Dark theme (drawing N1). The button is the switch; this only shows its state. */
    track: {
        position: 'relative',
        flexShrink: 0,
        width: '32px',
        height: '18px',
        boxSizing: 'border-box',
        borderRadius: '9px',
        border: `${tokens.strokeWidthThin} solid ${shellTokens.controlEdge}`,
    },
    trackOn: { backgroundColor: shellTokens.action, borderTopColor: shellTokens.action, borderRightColor: shellTokens.action, borderBottomColor: shellTokens.action, borderLeftColor: shellTokens.action },
    knob: {
        position: 'absolute',
        top: '3px',
        left: '3px',
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: shellTokens.text2,
    },
    knobOn: { left: '17px', backgroundColor: shellTokens.avatarForeground },
    account: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalMNudge,
        padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalXS} ${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalM}`,
        border: `${tokens.strokeWidthThin} solid ${shellTokens.divider}`,
        borderRadius: tokens.borderRadiusLarge,
    },
    avatar: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        flexShrink: 0,
        borderRadius: '50%',
        backgroundColor: shellTokens.avatarBackground,
        color: shellTokens.avatarForeground,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightBold,
    },
    buildLabel: {
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: tokens.spacingHorizontalS,
        padding: `0 ${tokens.spacingHorizontalL} ${tokens.spacingVerticalM}`,
        color: shellTokens.text3,
    },
    /** The site and batch in a word or two, centred and small enough that "Staging B12" breaks into two lines inside 56. */
    buildLabelRail: { display: 'flex', justifyContent: 'center', textAlign: 'center', paddingBottom: tokens.spacingVerticalS, paddingInline: tokens.spacingHorizontalXXS, color: shellTokens.text3 },
    buildTextRail: { fontSize: tokens.fontSizeBase100, lineHeight: tokens.lineHeightBase100 },
    buildDetail: { fontFamily: tokens.fontFamilyMonospace },
    accountText: { display: 'flex', flexDirection: 'column', minWidth: 0, flexGrow: 1 },
    accountName: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    main: { display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0, minHeight: 0 },
    header: {
        display: 'flex',
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.spacingHorizontalL,
        minHeight: shellMetrics.headerHeight,
        padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXXL} 0`,
    },
    headerMobile: {
        minHeight: shellMetrics.mobileHeaderHeight,
        backgroundColor: shellTokens.surface,
        borderBottom: `${tokens.strokeWidthThin} solid ${shellTokens.divider}`,
        padding: `0 ${tokens.spacingHorizontalS} 0 ${tokens.spacingHorizontalXS}`,
        gap: tokens.spacingHorizontalS,
        justifyContent: 'flex-start',
    },
    breadcrumb: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalSNudge, minWidth: 0, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, color: shellTokens.text3, flexGrow: 1 },
    // The shell owns the viewport; ordinary pages scroll here. Workspaces can fill the
    // available space and own their content scroll without calculating header offsets.
    content: { display: 'flex', flexDirection: 'column', padding: tokens.spacingHorizontalXXL, flexGrow: 1, minHeight: 0, overflowY: 'auto' },
    contentNarrow: { padding: tokens.spacingHorizontalL },
    scrim: { position: 'fixed', inset: 0, backgroundColor: shellTokens.scrim, zIndex: 20 },
    drawer: {
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: 0,
        width: shellMetrics.drawerWidth,
        maxWidth: '85vw',
        zIndex: 21,
        boxShadow: tokens.shadow28,
    },
});
/** How many rows `item` puts on screen in this form of the menu: the rail drops switched-off rows and flattens groups. */
function shownRows(item, variant) {
    const group = item.children ?? [];
    if (group.length === 0)
        return variant === 'rail' && item.unavailable !== undefined ? 0 : 1;
    if (variant === 'rail')
        return group.filter((child) => child.unavailable === undefined).length;
    return 1;
}
/** Runs of rows that share a group, in the order given. Rows with no group make runs of their own, with no heading. */
function sections(items) {
    const out = [];
    for (const item of items) {
        const last = out[out.length - 1];
        if (last !== undefined && last.group === item.group)
            last.items.push(item);
        else
            out.push({ group: item.group, items: [item] });
    }
    return out;
}
/**
 * Where the current page's row is inside the list, and whether the band may slide there.
 *
 * Measured after every render of the list (a new page re-renders it), and again when the list changes size (a
 * group opening, the menu folding). It slides only from one row to another: appearing, or coming back after the
 * current row was hidden in a shut group, it is simply there.
 */
function useActiveBand(listRef) {
    const [state, setState] = useState({ box: null, slide: false });
    const measure = useCallback(() => {
        const row = listRef.current?.querySelector('[aria-current="page"]') ?? null;
        const box = row === null ? null : { top: row.offsetTop, left: row.offsetLeft, width: row.offsetWidth, height: row.offsetHeight };
        setState((current) => {
            const prev = current.box;
            if (prev === null && box === null)
                return current;
            if (prev !== null && box !== null && prev.top === box.top && prev.left === box.left && prev.width === box.width && prev.height === box.height)
                return current;
            return { box, slide: prev !== null && box !== null };
        });
    }, [listRef]);
    useLayoutEffect(measure);
    useEffect(() => {
        const list = listRef.current;
        if (list === null || typeof ResizeObserver === 'undefined')
            return;
        const observer = new ResizeObserver(() => measure());
        observer.observe(list);
        return () => observer.disconnect();
    }, [listRef, measure]);
    return state;
}
function NavList({ items, Link, variant, onNavigate, still, }) {
    const s = useStyles();
    const listRef = useRef(null);
    const { box, slide } = useActiveBand(listRef);
    const headingId = useId();
    const runs = sections(items).filter((run) => run.items.some((item) => shownRows(item, variant) > 0));
    // A heading tells groups apart, so it is drawn only when the menu shows rows outside its group too.
    const headed = new Set(runs.map((run) => run.group)).size > 1;
    const rows = (run) => run.items.flatMap((item) => {
        const group = item.children ?? [];
        if (group.length === 0)
            return [_jsx(NavLeaf, { item: item, Link: Link, variant: variant, onNavigate: onNavigate }, item.key)];
        // The rail cannot nest, so it shows the destinations and drops the disclosure.
        if (variant === 'rail')
            return group.map((child) => _jsx(NavLeaf, { item: child, Link: Link, variant: variant, onNavigate: onNavigate }, child.key));
        return [_jsx(NavGroup, { item: item, Link: Link, variant: variant, onNavigate: onNavigate }, item.key)];
    });
    return (_jsxs("div", { ref: listRef, className: mergeClasses(s.list, variant === 'rail' && s.listRail), children: [box === null ? null : (_jsx("span", { "aria-hidden": "true", "data-testid": "shell-nav-band", className: s.band, style: {
                    width: `${String(box.width)}px`,
                    height: `${String(box.height)}px`,
                    transform: `translate(${String(box.left)}px, ${String(box.top)}px)`,
                    transition: slide && !still
                        ? `transform ${tokens.durationNormal} ${tokens.curveDecelerateMid}, width ${tokens.durationNormal} ${tokens.curveDecelerateMid}, height ${tokens.durationNormal} ${tokens.curveDecelerateMid}`
                        : 'none',
                }, children: _jsx("span", { className: s.bar }) })), runs.map((run, index) => {
                const key = run.group ?? `ungrouped-${String(index)}`;
                if (!headed || run.group === undefined)
                    return _jsx(Fragment, { children: rows(run) }, key);
                const id = `${headingId}-${String(index)}`;
                return variant === 'rail' ? (_jsxs("div", { role: "group", "aria-label": run.group, className: mergeClasses(s.section, s.sectionRail), children: [_jsx("div", { className: s.railDivider }), rows(run)] }, key)) : (_jsxs("div", { role: "group", "aria-labelledby": id, className: s.section, children: [_jsx("span", { id: id, className: s.heading, ...LABEL, children: run.group }), rows(run)] }, key));
            })] }));
}
function NavGroup({ item, Link, variant, onNavigate }) {
    if (item.unavailable !== undefined)
        return _jsx(NavGroupUnavailable, { item: item, reason: item.unavailable, Link: Link, variant: variant, onNavigate: onNavigate });
    return _jsx(NavGroupOpenable, { item: item, Link: Link, variant: variant, onNavigate: onNavigate });
}
/** A switched-off group: its row says why and opens nothing, and its destinations are listed beneath it, always. */
function NavGroupUnavailable({ item, reason, Link, variant, onNavigate }) {
    const s = useStyles();
    const Icon = ShellIcons[item.icon];
    return (_jsxs(_Fragment, { children: [_jsxs("span", { role: "button", "aria-disabled": "true", "aria-label": `${item.label}, ${reason}`, className: mergeClasses(s.item, s.itemUnavailable, variant === 'mobile' && s.itemMobile), children: [_jsx("span", { "aria-hidden": "true", className: s.icon, children: _jsx(Icon, {}) }), _jsx("span", { className: s.label, ...LABEL, children: item.label }), _jsx("span", { className: mergeClasses(s.caption, s.reason), ...LABEL, children: reason })] }), _jsx("div", { className: s.group, children: (item.children ?? []).map((child) => (_jsx(NavLeaf, { item: child, Link: Link, variant: variant, onNavigate: onNavigate, nested: true }, child.key))) })] }));
}
/**
 * A disclosure row and the destinations beneath it.
 *
 * It opens itself whenever one of its children is the current page, so arriving by a link from
 * elsewhere — or reloading — never leaves the group shut over the page you are looking at. Closing
 * it by hand still sticks while you stay on that page; only a CHANGE in which child is active
 * re-opens it.
 */
function NavGroupOpenable({ item, Link, variant, onNavigate }) {
    const s = useStyles();
    const children = item.children ?? [];
    const childActive = children.some((child) => child.active);
    const [open, setOpen] = useState(childActive);
    const [wasActive, setWasActive] = useState(childActive);
    if (childActive !== wasActive) {
        setWasActive(childActive);
        if (childActive)
            setOpen(true);
    }
    const Icon = ShellIcons[item.icon];
    const groupId = `shell-nav-${item.key}`;
    return (_jsxs(_Fragment, { children: [_jsxs("button", { type: "button", "aria-expanded": open, "aria-controls": groupId, onClick: () => setOpen((current) => !current), className: mergeClasses(s.item, s.groupToggle, variant === 'mobile' && s.itemMobile), children: [_jsx("span", { "aria-hidden": "true", className: s.icon, children: _jsx(Icon, {}) }), _jsx("span", { className: s.label, ...LABEL, children: item.label }), _jsx("span", { "aria-hidden": "true", className: mergeClasses(s.chevron, open && s.chevronOpen), ...LABEL, children: _jsx(ShellIcons.chevronDown, {}) })] }), open ? (_jsx("div", { id: groupId, className: s.group, children: children.map((child) => (_jsx(NavLeaf, { item: child, Link: Link, variant: variant, onNavigate: onNavigate, nested: true }, child.key))) })) : (_jsx("div", { id: groupId, hidden: true }))] }));
}
function NavLeaf({ item, Link, variant, onNavigate, nested }) {
    const s = useStyles();
    const Icon = ShellIcons[item.icon];
    const rail = variant === 'rail';
    if (item.unavailable !== undefined) {
        // The icon-only rail has no room for the reason, so it shows nothing: no icon, no gap, no tooltip and
        // nothing for the keyboard or a screen reader to land on. A group on the rail is already flattened to
        // its children in NavList, so this is the one place a switched-off row is dropped there (#634).
        if (rail)
            return null;
        // Not a <Link>: no address, no click, no tab stop. `role="link"` with `aria-disabled` is what makes a
        // screen reader say "unavailable" instead of reading two loose words.
        return (_jsxs("span", { role: "link", "aria-disabled": "true", "aria-label": `${item.label}, ${item.unavailable}`, className: mergeClasses(s.item, s.itemUnavailable, variant === 'mobile' && s.itemMobile, nested === true && s.itemNestedUnavailable), children: [nested === true ? null : _jsx("span", { "aria-hidden": "true", className: s.icon, children: _jsx(Icon, {}) }), _jsx("span", { className: s.label, ...LABEL, children: item.label }), _jsx("span", { className: mergeClasses(s.caption, s.reason), ...LABEL, children: item.unavailable })] }));
    }
    // The count is drawn as a badge only (drawing N3), so the words "7 waiting" are in the name instead, at every width.
    const name = item.badge ? `${item.label}, ${item.badge.label}` : item.label;
    const link = (_jsxs(Link, { to: item.to, "aria-current": item.active ? 'page' : undefined, "aria-label": rail || item.badge ? name : undefined, onClick: onNavigate, className: mergeClasses(s.item, item.active && s.itemActive, rail && s.itemRail, variant === 'mobile' && s.itemMobile, nested === true && !rail && s.itemNested), children: [nested === true && !rail ? null : _jsx("span", { "aria-hidden": "true", className: s.icon, children: _jsx(Icon, {}) }), rail ? null : _jsx("span", { className: s.label, ...LABEL, children: item.label }), item.badge ? (_jsx("span", { "aria-hidden": "true", className: mergeClasses(s.badge, rail && s.badgeRail), ...(rail ? {} : LABEL), children: item.badge.count > 99 ? '99+' : item.badge.count })) : null] }));
    // Rail items are icon-only: the tooltip is the accessible DESCRIPTION, the aria-label (above) is
    // the name, so screen readers hear the count without hovering.
    return rail ? (_jsx(Tooltip, { content: name, relationship: "description", positioning: "after", children: link })) : (link);
}
function initials(name) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('');
}
/** The foot of the menu: Dark theme above the person's name, role and one-click Sign out (choice 1 B on #715). */
function Utility({ account, buildLabel, variant, }) {
    const s = useStyles();
    const { scheme, toggle } = useColorScheme();
    const rail = variant === 'rail';
    const dark = scheme === 'dark';
    const themeButton = (_jsxs("button", { type: "button", role: "switch", "aria-checked": dark, "aria-label": "Dark theme", onClick: toggle, className: mergeClasses(s.control, rail ? s.controlRail : s.themeRow), children: [_jsx(ShellIcons.moon, {}), rail ? null : (_jsxs(_Fragment, { children: [_jsx("span", { className: s.label, ...LABEL, children: "Dark theme" }), _jsx("span", { "aria-hidden": "true", className: mergeClasses(s.track, dark && s.trackOn), ...LABEL, children: _jsx("span", { className: mergeClasses(s.knob, dark && s.knobOn) }) })] }))] }));
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: mergeClasses(s.utility, rail && s.utilityRail), children: [rail ? (_jsx(Tooltip, { content: "Dark theme", relationship: "description", positioning: "after", children: themeButton })) : (themeButton), account ? (rail ? (_jsx(Tooltip, { content: `${account.displayName} · ${account.roleLabel} · Sign out`, relationship: "description", positioning: "after", children: _jsx("a", { href: account.signOutHref, "aria-label": `Account: ${account.displayName}, ${account.roleLabel}. Sign out`, className: mergeClasses(s.control, s.controlRail), children: _jsx("span", { className: s.avatar, children: initials(account.displayName) }) }) })) : (_jsxs("div", { className: s.account, children: [_jsx("span", { "aria-hidden": "true", className: s.avatar, children: initials(account.displayName) }), _jsxs("div", { className: s.accountText, ...LABEL, children: [_jsx("span", { className: s.accountName, children: account.displayName }), _jsx("span", { className: s.caption, children: account.roleLabel })] }), _jsx("a", { href: account.signOutHref, "aria-label": "Sign out", title: "Sign out", className: mergeClasses(s.control, s.controlRail), ...LABEL, children: _jsx(ShellIcons.signOut, {}) })] }))) : null] }), buildLabel ? (rail ? (
            // Icons-only menu: a few letters, with the whole sentence as its name and its tooltip.
            _jsx("div", { className: s.buildLabelRail, children: _jsx("span", { role: "img", "aria-label": buildLabel.fullText, title: buildLabel.fullText, className: mergeClasses(s.caption, s.buildTextRail), children: buildLabel.railText }) })) : (_jsxs("div", { className: s.buildLabel, "data-testid": "shell-build-label", ...LABEL, children: [_jsx("span", { className: s.caption, children: buildLabel.text }), buildLabel.detail ? _jsx("span", { className: mergeClasses(s.caption, s.buildDetail), children: buildLabel.detail }) : null] }))) : null] }));
}
/**
 * VIGEO HR, and beside it the menu's own button: fold and unfold on a desktop, close in the phone drawer (#742).
 *
 * The same elements in both forms, so the fold button keeps the keyboard's focus when it is pressed: it is always
 * wrapped in its tooltip, since a wrapper that came and went would make it a new button.
 */
function Identity({ Link, caption, rail, button, onBrand, }) {
    const s = useStyles();
    return (_jsxs("div", { className: mergeClasses(s.identity, rail && s.identityRail), children: [_jsxs(Link, { to: "/app", className: mergeClasses(s.brandLink, rail && s.brandLinkRail), "aria-label": "VIGEO HR home", onClick: onBrand, children: [_jsx("span", { "aria-hidden": "true", className: s.mark, children: "V" }), rail ? null : (_jsxs("span", { className: s.brandText, ...LABEL, children: [_jsx("span", { className: s.brandName, children: "VIGEO HR" }), _jsx("span", { className: s.caption, children: caption })] }))] }), button] }));
}
export function AppShell({ nav, Link = AnchorLink, currentPath, account, buildLabel, caption = DEFAULT_CAPTION, breadcrumb, pageAction, children, }) {
    const s = useStyles();
    const { tier, expanded, canToggle, setExpanded } = useShellLayout();
    const still = usePrefersReducedMotion();
    const fold = useFold(expanded, still);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const menuRef = useRef(null);
    const closeRef = useRef(null);
    const dialogRef = useRef(null);
    const contentRef = useRef(null);
    const drawerId = useId();
    const mobile = tier === 'mobile';
    // The drawer is open only at the phone width, and a change of width tier SHUTS it: the open state is cleared here, so
    // coming back to the phone width draws the closed drawer instead of re-opening the one left behind.
    const [lastTier, setLastTier] = useState(tier);
    if (tier !== lastTier) {
        setLastTier(tier);
        setDrawerOpen(false);
    }
    const drawerShown = mobile && drawerOpen;
    const closeMenu = useCallback(() => {
        setDrawerOpen(false);
        // Focus returns to the header's button that opened the drawer.
        requestAnimationFrame(() => menuRef.current?.focus());
    }, []);
    // A destination starts at its beginning; query-driven workspace sections own their scroll.
    useEffect(() => {
        setDrawerOpen(false);
        if (contentRef.current)
            contentRef.current.scrollTop = 0;
    }, [currentPath]);
    // Open: focus the close button, lock body scroll, make the page inert. Escape closes.
    useEffect(() => {
        if (!drawerShown)
            return;
        closeRef.current?.focus();
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (event) => {
            if (event.key === 'Escape')
                closeMenu();
            if (event.key !== 'Tab')
                return;
            // Tab goes round inside the open drawer rather than out into the browser's own controls.
            const stops = [...(dialogRef.current?.querySelectorAll('a[href], button:not([disabled])') ?? [])];
            const first = stops[0];
            const last = stops[stops.length - 1];
            if (first === undefined || last === undefined)
                return;
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKey);
        };
    }, [drawerShown, closeMenu]);
    // The width follows the choice at once; what is drawn inside follows the fold (motion.ts).
    const variant = fold.drawn;
    const foldLabel = expanded ? 'Collapse navigation' : 'Expand navigation';
    const sidebar = mobile ? null : (_jsxs("nav", { "aria-label": "Primary", className: mergeClasses(s.nav, expanded ? s.navExpanded : s.navRail, !still && s.navMoving, fold.phase === 'folding' && s.navFolding, fold.phase === 'unfolding' && s.navUnfolding), children: [_jsx(Identity, { Link: Link, caption: caption, rail: variant === 'rail', 
                // At every width with a side menu the same button folds and unfolds it in place, and it keeps the keyboard's focus.
                // The menu is part of the page, not a dialog, so Escape does not fold it (#754).
                button: canToggle ? (_jsx(Tooltip, { content: foldLabel, relationship: "description", positioning: "after", children: _jsx("button", { type: "button", "aria-label": foldLabel, "aria-expanded": expanded, onClick: () => setExpanded(!expanded), className: mergeClasses(s.control, variant === 'rail' ? s.controlRail : s.controlSmall), children: _jsx(ShellIcons.panel, {}) }) })) : null }), variant === 'rail' ? _jsx("div", { className: s.railDivider }) : null, _jsx(NavList, { items: nav, Link: Link, variant: variant, still: still }), _jsx("div", { className: s.spacer }), _jsx(Utility, { account: account, buildLabel: buildLabel, variant: variant })] }));
    const drawer = drawerShown ? (_jsxs(_Fragment, { children: [_jsx("div", { className: s.scrim, onClick: closeMenu, "data-testid": "shell-scrim" }), _jsx("div", { ref: dialogRef, id: drawerId, role: "dialog", "aria-modal": "true", "aria-label": "Navigation", className: s.drawer, children: _jsxs("nav", { "aria-label": "Primary", className: mergeClasses(s.nav, s.navExpanded), style: { width: '100%' }, children: [_jsx(Identity, { Link: Link, caption: caption, rail: false, onBrand: closeMenu, button: (_jsx("button", { ref: closeRef, type: "button", "aria-label": "Close navigation", onClick: closeMenu, className: mergeClasses(s.control, s.controlRail), children: _jsx(ShellIcons.close, {}) })) }), _jsx(NavList, { items: nav, Link: Link, variant: "mobile", onNavigate: closeMenu, still: still }), _jsx("div", { className: s.spacer }), _jsx(Utility, { account: account, buildLabel: buildLabel, variant: "mobile" })] }) })] })) : null;
    return (_jsxs("div", { className: s.shell, children: [sidebar, drawer, _jsxs("div", { className: s.main, inert: drawerShown ? true : undefined, children: [_jsxs("header", { className: mergeClasses(s.header, mobile && s.headerMobile), children: [mobile ? (_jsx("button", { ref: menuRef, type: "button", "aria-label": "Open navigation", "aria-expanded": drawerOpen, "aria-controls": drawerId, onClick: () => setDrawerOpen(true), className: mergeClasses(s.control, s.controlRail), children: _jsx(ShellIcons.menu, {}) })) : null, _jsx("div", { className: s.breadcrumb, children: breadcrumb }), pageAction ?? null] }), _jsx("main", { ref: contentRef, className: mergeClasses(s.content, tier !== 'wide' && tier !== 'desktop' && s.contentNarrow), children: children })] })] }));
}
//# sourceMappingURL=AppShell.js.map